import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { inferRouterOutputs } from '@trpc/server'
import type { AppRouter } from '@viapeople/api'
import { Button, Modal, SpinnerWithBackground, Text } from '@viapeople/components'
import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useToast } from '../../utils/ToastContext'
import { trpc } from '../../utils/trpc'
import { type ConnectionSettingsData, ConnectionSettingsStep } from './steps/ConnectionSettingsStep'
import { CycleTemplateStep } from './steps/CycleTemplateStep'
import { DataMappingStep } from './steps/DataMappingStep'
import { RoleMappingStep } from './steps/RoleMappingStep'
import { TriggerRulesStep } from './steps/TriggerRulesStep'
import type { CrmProvider, EventMapping, FieldMapping, RoleMapping } from './types'
import { WizardNavigation } from './WizardNavigation'
import { WizardStepIndicator } from './WizardStepIndicator'
import { normalizeFieldMappings, parseRoleMappings } from './wizardUtils'

const WIZARD_STEPS = [
	{ id: 'connection', label: 'Connection Settings' },
	{ id: 'triggers', label: 'Trigger Rules' },
	{ id: 'mapping', label: 'Data Mapping' },
	{ id: 'template', label: 'Cycle Template' },
	{ id: 'roles', label: 'Role Mapping' },
]

interface IntegrationWizardProps {
	isOpen: boolean
	onClose: () => void
	onComplete: () => void
	editingIntegrationId?: string
}

type RouterOutput = inferRouterOutputs<AppRouter>
type CrmIntegrationDetails = RouterOutput['admin']['integrations']['crm']['getById']

export function IntegrationWizard({ isOpen, onClose, onComplete, editingIntegrationId }: IntegrationWizardProps) {
	const { organizationId } = useParams<{ organizationId: string }>()
	const { showToast } = useToast()
	const queryClient = useQueryClient()
	const isEditMode = !!editingIntegrationId

	const [currentStep, setCurrentStep] = useState(0)

	// Step 1: Connection Settings
	const [connectionData, setConnectionData] = useState<ConnectionSettingsData>({
		name: '',
		provider: 'salesforce' as CrmProvider,
	})

	// Step 2: Trigger Rules
	const [eventMappings, setEventMappings] = useState<EventMapping[]>([])

	// Step 3: Data Mapping
	const [fieldMappings, setFieldMappings] = useState<FieldMapping[]>([])

	// Step 4: Cycle Template
	const [selectedTemplateId, setSelectedTemplateId] = useState('')

	// Step 5: Role Mapping
	const [roleMappings, setRoleMappings] = useState<RoleMapping[]>([])

	// Track if edit data has been loaded
	const [editDataLoaded, setEditDataLoaded] = useState(false)

	// Track created integration ID in case mapping save fails so we can update instead of duplicate
	const [createdIntegrationId, setCreatedIntegrationId] = useState<string | null>(null)

	const effectiveIntegrationId = isEditMode ? editingIntegrationId : createdIntegrationId
	const isEffectiveEditMode = isEditMode || !!createdIntegrationId

	// Load existing integration data in edit mode (getById returns { integration, fieldMappings, eventMappings })
	const { data: existingData, isLoading: isLoadingEditData } = useQuery({
		...trpc.admin.integrations.crm.getById.queryOptions({ id: editingIntegrationId as string }),
		enabled: isEditMode && !!editingIntegrationId,
	})

	// Pre-populate form when edit data loads
	useEffect(() => {
		if (isEditMode && existingData && !editDataLoaded) {
			const { integration, fieldMappings: fMappings, eventMappings: eMappings } = existingData as CrmIntegrationDetails

			if (integration) {
				setConnectionData({
					name: integration.name,
					provider: integration.provider as CrmProvider,
					connectionId: integration.connectionId || undefined,
				})
				setSelectedTemplateId(integration.templateId || integration.targetCycleId || '')
			}

			if (fMappings && fMappings.length > 0) {
				setFieldMappings(normalizeFieldMappings(fMappings))
			}

			if (eMappings && eMappings.length > 0) {
				setEventMappings(
					eMappings.map((m) => ({
						crmEvent: m.crmEvent,
						viaPeopleEvent: m.viaPeopleEvent,
						enabled: m.enabled,
						triggerField: m.triggerField ?? undefined,
						triggerValues: m.triggerValues ?? undefined,
					})),
				)
			}

			if (Array.isArray(integration.crmRoleMappings)) {
				const valid = parseRoleMappings(integration.crmRoleMappings as unknown[])
				if (valid.length > 0) setRoleMappings(valid)
			}

			setEditDataLoaded(true)
		}
	}, [isEditMode, existingData, editDataLoaded])

	// Mapping mutations
	const fieldMappingsMutation = useMutation(trpc.admin.integrations.crm.upsertFieldMappings.mutationOptions())
	const eventMappingsMutation = useMutation(trpc.admin.integrations.crm.upsertEventMappings.mutationOptions())
	const roleMappingsMutation = useMutation(trpc.admin.integrations.crm.upsertRoleMappings.mutationOptions())

	// Create mutation (new mode only)
	const createMutation = useMutation({
		...trpc.admin.integrations.crm.create.mutationOptions(),
		onSuccess: async (data) => {
			const integrationId = (data as { id: string }).id
			setCreatedIntegrationId(integrationId)
			await saveMappings(integrationId)
		},
		onError: (error) => {
			showToast(error.message || 'Failed to create integration. Please try again.', 'error')
		},
	})

	// Update mutation (edit mode only)
	const updateMutation = useMutation({
		...trpc.admin.integrations.crm.update.mutationOptions(),
		onSuccess: async () => {
			await saveMappings(effectiveIntegrationId as string)
		},
		onError: (error) => {
			showToast(error.message || 'Failed to update integration. Please try again.', 'error')
		},
	})

	const saveMappings = async (integrationId: string) => {
		try {
			// Save field mappings. Both crmField and viaPeopleField must be filled: the API
			// schema requires non-empty source/target, so half-filled custom rows would crash the
			// whole save with a Zod error instead of being silently dropped here.
			const validFieldMappings = fieldMappings
				.filter((m) => m.crmField && m.viaPeopleField)
				.map((m) => ({
					sourceField: m.crmField,
					targetField: m.viaPeopleField,
					transform: m.transform || undefined,
				}))

			if (validFieldMappings.length > 0) {
				await fieldMappingsMutation.mutateAsync({
					integrationId,
					mappings: validFieldMappings,
				})
			}

			// Save event mappings.
			//
			// Parse triggerValues at save time:
			//   - prefer triggerValuesRaw if the user edited the input (it contains the
			//     verbatim comma-separated string and preserves trailing commas)
			//   - fall back to the loaded-from-server triggerValues array
			// Whichever source we use, split on comma, trim, drop empties.
			const enabledEventMappings = eventMappings
				.filter((m) => m.enabled)
				.map((m) => {
					const triggerField = m.triggerField?.trim()
					const rawValues = m.triggerValuesRaw ?? (m.triggerValues ?? []).join(', ')
					const triggerValues = rawValues
						.split(',')
						.map((v) => v.trim())
						.filter((v) => v.length > 0)
					return {
						crmEvent: m.crmEvent,
						viaPeopleEvent: m.viaPeopleEvent,
						triggerField: triggerField || undefined,
						triggerValues: triggerValues.length > 0 ? triggerValues : undefined,
					}
				})

			if (enabledEventMappings.length > 0) {
				await eventMappingsMutation.mutateAsync({
					integrationId,
					mappings: enabledEventMappings,
				})
			}

			// Save role mappings (always upsert so removals are persisted too)
			const validRoleMappings = roleMappings.filter((m) => m.crmRoleValue && m.roleId && m.formId)
			await roleMappingsMutation.mutateAsync({ integrationId, mappings: validRoleMappings })

			showToast(isEditMode ? 'Integration updated successfully' : 'Integration created successfully')
			queryClient.invalidateQueries({
				queryKey: trpc.admin.integrations.crm.getByOrganization.queryKey({
					organizationId: organizationId as string,
				}),
			})
			// Also invalidate the detail query so next edit loads fresh data
			if (integrationId) {
				queryClient.invalidateQueries({
					queryKey: trpc.admin.integrations.crm.getById.queryKey({ id: integrationId }),
				})
			}
			resetWizard()
			onComplete()
		} catch (error) {
			console.error('Failed to save mappings:', error)
			showToast(
				`Integration ${isEditMode ? 'updated' : 'created'} but failed to save mappings. Please try again.`,
				'error',
			)
			// Do not close wizard so user can retry
		}
	}

	// Delete mutation for cleanup on cancel
	const deleteMutation = useMutation({
		...trpc.admin.integrations.crm.delete.mutationOptions(),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: trpc.admin.integrations.crm.getByOrganization.queryKey({
					organizationId: organizationId as string,
				}),
			})
			resetWizard()
			onClose()
		},
		onError: () => {
			showToast('Failed to clean up partial integration. You may need to delete it manually.', 'error')
			resetWizard()
			onClose()
		},
	})

	const resetWizard = () => {
		setCurrentStep(0)
		setConnectionData({ name: '', provider: 'salesforce' })
		setEventMappings([])
		setFieldMappings([])
		setSelectedTemplateId('')
		setRoleMappings([])
		setEditDataLoaded(false)
		setCreatedIntegrationId(null)
	}

	const handleClose = () => {
		if (createdIntegrationId) {
			deleteMutation.mutate({ id: createdIntegrationId })
		} else {
			resetWizard()
			onClose()
		}
	}

	const handleNext = () => {
		if (currentStep < WIZARD_STEPS.length - 1) {
			setCurrentStep(currentStep + 1)
		}
	}

	const handleBack = () => {
		if (currentStep > 0) {
			setCurrentStep(currentStep - 1)
		}
	}

	const handleSave = () => {
		if (isEffectiveEditMode) {
			updateMutation.mutate({
				id: effectiveIntegrationId as string,
				name: connectionData.name,
				templateId: selectedTemplateId || undefined,
				connectionId: connectionData.connectionId || undefined,
			})
		} else {
			createMutation.mutate({
				organizationId: organizationId as string,
				name: connectionData.name,
				provider: connectionData.provider,
				templateId: selectedTemplateId || undefined,
				connectionId: connectionData.connectionId,
			})
		}
	}

	const isStepValid = (step: number): boolean => {
		switch (step) {
			case 0:
				if (isEditMode) {
					return !!connectionData.name?.trim()
				}
				if (connectionData.provider === 'salesforce') {
					return !!connectionData.name?.trim() && !!connectionData.provider && !!connectionData.connectionId
				}
				return !!connectionData.name?.trim() && !!connectionData.provider
			case 1:
				return eventMappings.some((m) => m.enabled)
			case 2:
				return fieldMappings.filter((m) => m.crmField && m.viaPeopleField).length > 0
			case 3:
				return !!selectedTemplateId
			case 4:
				return true // Role mapping is optional
			default:
				return false
		}
	}

	const isLastStep = currentStep === WIZARD_STEPS.length - 1
	const isSaving = createMutation.isPending || updateMutation.isPending

	return (
		<Modal
			isOpen={isOpen}
			onClose={handleClose}
			title={isEditMode ? 'Edit CRM Integration' : 'New CRM Integration'}
			className='!w-[700px] md:!w-[800px]'
			closeOnOverlayClick={false}>
			<div className='flex flex-col min-h-[400px]'>
				<WizardStepIndicator steps={WIZARD_STEPS} activeStep={currentStep} />

				<div className='h-px bg-neutral-100 w-full' />

				{/* Step Content */}
				<div className='flex-1 py-6 overflow-y-auto'>
					{isLoadingEditData || (isEditMode && !editDataLoaded) ? (
						<SpinnerWithBackground backgroundClass='opacity-100' />
					) : (
						<>
							{currentStep === 0 && (
								<ConnectionSettingsStep data={connectionData} onChange={setConnectionData} isEditMode={isEditMode} />
							)}
							{currentStep === 1 && (
								<TriggerRulesStep
									provider={connectionData.provider}
									eventMappings={eventMappings}
									onChange={setEventMappings}
								/>
							)}
							{currentStep === 2 && (
								<DataMappingStep
									provider={connectionData.provider}
									connectionId={connectionData.connectionId}
									fieldMappings={fieldMappings}
									onChange={setFieldMappings}
								/>
							)}
							{currentStep === 3 && (
								<CycleTemplateStep selectedTemplateId={selectedTemplateId} onChange={setSelectedTemplateId} />
							)}
							{currentStep === 4 && (
								<RoleMappingStep
									roleMappings={roleMappings}
									onChange={setRoleMappings}
									provider={connectionData.provider}
									connectionId={connectionData.connectionId}
								/>
							)}
						</>
					)}
				</div>

				<WizardNavigation
					currentStep={currentStep}
					onBack={handleBack}
					onClose={handleClose}
					actionButton={
						isLastStep ? (
							<Button variant='primary' onClick={handleSave} disabled={!isStepValid(currentStep) || isSaving}>
								{isSaving
									? isEditMode
										? 'Saving...'
										: 'Creating...'
									: isEditMode
										? 'Save Changes'
										: 'Create Integration'}
							</Button>
						) : (
							<Button variant='primary' onClick={handleNext} disabled={!isStepValid(currentStep)}>
								Next →
							</Button>
						)
					}
				/>

				{/* Validation hint */}
				{!isStepValid(currentStep) && (
					<div className='mt-2'>
						<Text variant='xsmall' className='text-neutral-400 text-right'>
							{currentStep === 0 && 'Enter an integration name and select a provider'}
							{currentStep === 1 && 'Enable at least one trigger rule'}
							{currentStep === 2 && 'Map at least one CRM field'}
							{currentStep === 3 && 'Select a cycle template'}
						</Text>
					</div>
				)}
			</div>

			{isSaving && <SpinnerWithBackground />}
		</Modal>
	)
}
